/**
 * Enviar las notas por correo: cada destinatario recibe el resumen, las tareas
 * y un link de solo lectura que vence en 30 días. Las respuestas llegan a ti.
 */
import { useState } from 'react'
import { errorText, useApp, useToast } from './AppContext'
import { Modal } from './ui'

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function SendNotesDialog({
  open,
  onClose,
  recordingId,
  onSent
}: {
  open: boolean
  onClose: () => void
  recordingId: string
  onSent: () => void
}) {
  const { api } = useApp()
  const toast = useToast()
  const [para, setPara] = useState('')
  const [mensaje, setMensaje] = useState('')
  const [busy, setBusy] = useState(false)
  const correos = para
    .split(/[\s,;]+/)
    .map((x) => x.trim())
    .filter(Boolean)
  const invalidos = correos.filter((c) => !EMAIL.test(c))

  async function send() {
    setBusy(true)
    try {
      const r = await api.sendNotes(recordingId, {
        destinatarios: correos,
        ...(mensaje.trim() ? { mensaje: mensaje.trim() } : {})
      })
      toast(
        r.fallidos.length
          ? `Enviado a ${r.enviados}; falló: ${r.fallidos.join(', ')}`
          : `Notas enviadas a ${r.enviados} ${r.enviados === 1 ? 'persona' : 'personas'}`,
        r.fallidos.length ? 'error' : 'ok'
      )
      setPara('')
      setMensaje('')
      onSent()
      onClose()
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Enviar las notas por correo"
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="btn primary"
            disabled={busy || !correos.length || invalidos.length > 0 || correos.length > 10}
            onClick={() => void send()}
          >
            {busy ? 'Enviando…' : 'Enviar'}
          </button>
        </>
      }
    >
      <div className="stack">
        <label className="field">
          <span>Para (hasta 10 correos, separados por coma)</span>
          <textarea
            rows={2}
            value={para}
            onChange={(e) => setPara(e.target.value)}
            placeholder="ana@empresa.cl, luis@cliente.cl"
          />
        </label>
        {invalidos.length > 0 && (
          <p className="notice error" style={{ margin: 0 }}>
            Revisa: {invalidos.join(', ')}
          </p>
        )}
        {correos.length > 10 && (
          <p className="notice error" style={{ margin: 0 }}>
            Son {correos.length} correos: el máximo es 10 por envío.
          </p>
        )}
        <label className="field">
          <span>Mensaje (opcional)</span>
          <textarea
            rows={3}
            maxLength={500}
            value={mensaje}
            onChange={(e) => setMensaje(e.target.value)}
          />
        </label>
        <p className="faint" style={{ fontSize: 12, margin: 0 }}>
          Reciben el resumen, las tareas y un link de solo lectura a las notas que vence en 30 días
          (sin la transcripción). Si responden, el correo te llega a ti.
        </p>
      </div>
    </Modal>
  )
}
